import { useState, useMemo, useCallback } from 'react';
import { useParams, useRouter, useSearchParams } from 'next/navigation';
import { getConvertedSummary } from '../../../utils/getConvertedSummary';
import { getInputValueFromLocalStorage } from '../../../utils/localStorageInputValueUtils';
import {
  TradeFormProps,
  SP_WEEKLY_FREQUENCY,
  SP_BIWEEKLY_FREQUENCY,
  SP_DAILY_FREQUENCY,
} from '../../types';
import {
  AccessInfoType,
  getAmountInputPlaceHolder,
  useHandleGqlErrorsWithSnackbar,
  UserAccessModal,
} from '../../../../../common';
import { useTradeInput } from '../useTradeInput';
import { getYmdForNthDayFromToday } from '../../../utils/dateUtils';
import { createTradeOffer } from './createTradeHandlers/createTradeOffer';
import {
  AmountFor,
  OrderOperation,
  SavingsPlanFrequency,
  TradeOfferOperation,
} from '@bts-web/data-layer/server';
import { AssetType } from '../../../../../asset-details/types';
import { useClientUserInfo } from '../../../../../common/userinfo/InitialUserInfoFetcherAndParser/utils/useClientUserInfo';
import { getAssetTradeUrl } from '../../../../../route-builders';
import { getAppropriatenessRedirectUrl } from '../../../utils/getAppropriatenessRedirectUrl';
import { useModalDrawerContext } from '../../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { createSavingsPlan } from './createTradeHandlers/createSavingsPlan';
import { createOrder } from './createTradeHandlers/createOrder';
import { TARGET_PRICE_KEY } from '../../../constants';
import { TradeControllerVariants, TradeOperation } from '../../../../utils';
import { format } from 'date-fns';
import { getAppConfig } from '@bts-web/core-app-config';
import { useFeatureFlag } from '../../../../../statsig/sdk/provider';

export type IUseTradeForm = Pick<
  TradeFormProps,
  'assetDetails' | 'targetAssetData' | 'currentTradeDetails' | 'staticValues'
>;

export const useTradeForm = ({
  assetDetails,
  currentTradeDetails,
  staticValues,
  targetAssetData,
}: IUseTradeForm) => {
  const { minimumCalendarDaysBeforeExecution = 4 } = getAppConfig();

  const feature_hasOrdersEnabled = useFeatureFlag('feature_hasOrdersEnabled');

  const feature_showSegmentLimitsError = useFeatureFlag(
    'feature_showSegmentLimitsError',
  );

  const feature_enableFiatTransfers = useFeatureFlag(
    'feature_enableFiatTransfers',
  );

  const routeParams = useParams();

  const processedRouteParams = useMemo(() => {
    const params = routeParams as Record<string, string>;

    return {
      operation: params.operation ?? staticValues.transactionType,
      assetId: params.assetId,
    };
  }, [routeParams]);

  const searchParams = useSearchParams();

  const router = useRouter();

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  const [isLoading, setIsLoading] = useState(false);

  const [savingsPlanRecurringDate, setSavingsPlanRecurringDate] =
    useState<string>(
      getYmdForNthDayFromToday(minimumCalendarDaysBeforeExecution),
    );

  const [savingsPlanFrequency, setSavingsPlanFrequency] =
    useState<SavingsPlanFrequency>('MONTHLY');

  const amountSearchParam = searchParams.get('amount') || '';

  const isTargetPricePage = searchParams.has(TARGET_PRICE_KEY);

  const isOrder = staticValues.transactionType === TradeOperation.order;

  const amountValueFromSearchParams =
    isNaN(Number(amountSearchParam)) || isTargetPricePage
      ? ''
      : amountSearchParam;

  const getInitialInputValue = (assetId: string) => {
    return (
      getInputValueFromLocalStorage({ assetId }) ||
      amountValueFromSearchParams ||
      ''
    );
  };

  const providedInputValue =
    isOrder || isTargetPricePage ? '' : getInitialInputValue(assetDetails.id);

  const [initialInputValue] = useState(providedInputValue);

  const { showModal } = useModalDrawerContext();

  const {
    isReadOnlyUser,
    approved,
    appropriatenessRequiresTestCompletion,
    appropriatenessRequiresTestResubmission,
  } = useClientUserInfo();

  const {
    onTradeInputChange,
    onQuickSelectButtonClick,
    displayType,
    onInputBlur,
    inputValue,
    errors,
    setErrors,
    onChangeDisplayType,
  } = useTradeInput({
    rootMaximumTradeAmount: currentTradeDetails.maximumTradeAmount,
    rootMinimumTradeAmount: currentTradeDetails.minimumTradeAmount,
    price: assetDetails.price,
    targetAssetPrice: targetAssetData?.price,
    initialInputValue,
    transactionType: staticValues.transactionType,
  });

  const onFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (isReadOnlyUser) {
        showModal(UserAccessModal, {
          type: AccessInfoType.ReadOnly,
        });

        return;
      }

      setIsLoading(true);

      setErrors((state) => ({ ...state, customError: null }));

      const formData = new FormData(e.currentTarget);

      const { operation, assetId } = processedRouteParams ?? {};

      const appropriatenessUrl = getAppropriatenessRedirectUrl({
        assetType: targetAssetData?.__typename
          ? targetAssetData?.__typename
          : (assetDetails.__typename as string),
        searchParams: searchParams as unknown as Record<string, string>,
        appropriatenessStatus: {
          appropriatenessRequiresTestCompletion,
          appropriatenessRequiresTestResubmission,
        },
        assetId,
        operation: operation as TradeControllerVariants,
      });

      if (appropriatenessUrl) {
        router.push(appropriatenessUrl);

        return;
      }

      const formattedAmount = (formData.get('amount') as string).replace(
        /,/g,
        '.',
      );

      const assetDetailsBase = getAssetTradeUrl(assetDetails.id, {
        type: 'assetDetails',
      });

      switch (staticValues.transactionType) {
        case 'buy':

        // eslint-disable-next-line no-fallthrough
        case 'sell': {
          createTradeOffer(
            formData,
            {
              currentTradeDetails,
              assetDetails,
              transactionType:
                staticValues.transactionType.toUpperCase() as TradeOfferOperation,
            },
            {
              setIsLoading,
              setErrors,
              processErrors,
              router,
              showModal,
            },
            { feature_showSegmentLimitsError, feature_enableFiatTransfers },
          );

          break;
        }

        case 'order': {
          // if on target price page and the amount input is provided, start the order creation flow
          if (amountSearchParam && isTargetPricePage && formattedAmount) {
            createOrder(
              {
                assetDetails,
                amount: amountSearchParam,
                targetPrice: formattedAmount,
                orderOperation: staticValues.orderOperation as OrderOperation,
              },
              {
                setIsLoading,
                setErrors,
                processErrors,
                router,
                showModal,
              },
              {
                feature_hasOrdersEnabled,
                feature_showSegmentLimitsError,
              },
            );
          } else if (staticValues.orderOperation) {
            // otherwise, redirect to target price page when orderType is set (order flow)
            const redirectUrl = `${getAssetTradeUrl(assetDetails.id, {
              type: 'orderForm',
              orderType: staticValues.orderOperation,
              amount: formattedAmount,
            })}&${TARGET_PRICE_KEY}`;

            router.push(redirectUrl);
          }

          break;
        }

        case 'savings': {
          createSavingsPlan(
            formData,
            { assetDetails },
            { setIsLoading, setErrors, processErrors, router },
          );

          break;
        }

        case 'swap': {
          const amountFor =
            (formData.get('amountFor') as AmountFor) === 'ASSET'
              ? 'source'
              : 'target';

          router.push(
            `${assetDetailsBase}/swap/accept_swap?targetAssetId=${targetAssetData?.id}&amount=${formattedAmount}&amountFor=${amountFor}`,
          );

          break;
        }

        case 'stake': {
          router.push(
            `${assetDetailsBase}/stake/accept_stake?amount=${formattedAmount}`,
          );

          break;
        }

        case 'unstake': {
          router.push(
            `${assetDetailsBase}/unstake/accept_unstake?amount=${formattedAmount}`,
          );

          break;
        }

        default:
          break;
      }
    },
    [
      assetDetails,
      currentTradeDetails,
      targetAssetData,
      staticValues.orderOperation,
    ],
  );

  const inputPlaceholder = useMemo(() => {
    return getAmountInputPlaceHolder({
      displayType: displayType,
      locale: staticValues.currentLocale,
      symbol: assetDetails.symbol as string,
      ...(staticValues.transactionType === TradeOperation.swap
        ? {
            targetSymbol: assetDetails?.symbol || '',
          }
        : {
            currency: staticValues.currency,
          }),
    });
  }, [
    assetDetails.symbol,
    targetAssetData?.symbol,
    displayType,
    staticValues.currency,
    staticValues.currentLocale,
  ]);

  const formHasAnyErrors = errors.tooHigh || errors.tooLow;

  const allButtonsDisabled =
    // ? DO NOT REMOVE BELOW CHECK; for LBBW, a business user goes through 2 phases: business-approval-pending first and then read-only mode
    // ? In the first phase, button is disabled but after being approved and becoming read-only, the button can be clicked and it shows the Read-Only modal
    (isReadOnlyUser && !approved) ||
    currentTradeDetails.userCannotTrade ||
    assetDetails.assetIsNotTradeable;

  const inputContextData = useMemo(
    () => ({
      currency: staticValues.currency,
      currentLocale: staticValues.currentLocale,
      displayType,
      symbol: assetDetails.symbol as string,

      value: inputValue,
      placeholder: inputPlaceholder,
      transactionType: staticValues.transactionType,
    }),
    [
      staticValues.currency,
      staticValues.currentLocale,
      displayType,
      assetDetails.symbol,
      inputValue,
      inputPlaceholder,
      staticValues.transactionType,
    ],
  );

  const convertedSummary = useMemo(
    () =>
      getConvertedSummary({
        assetPrice: assetDetails.price,
        assetType: assetDetails.assetType as AssetType,
        tradeType: staticValues.transactionType,
        currency: staticValues.currency,
        displayType,
        locale: staticValues.currentLocale,
        symbol: assetDetails.symbol,
        ...(targetAssetData && currentTradeDetails.swapExchangeRate
          ? {
              swap: {
                targetSymbol: targetAssetData.symbol as string,
                swapExchangeRate: currentTradeDetails.swapExchangeRate,
              },
            }
          : {}),
        translations: staticValues.translations,
        value: inputValue,
        maximumTradeAmount: currentTradeDetails.maximumTradeAmount,
      }),
    [
      assetDetails.price,
      assetDetails.assetType,
      assetDetails.symbol,
      staticValues.transactionType,
      staticValues.currency,
      staticValues.currentLocale,
      staticValues.translations,
      displayType,
      inputValue,
      currentTradeDetails.maximumTradeAmount,
      currentTradeDetails.swapExchangeRate,
      targetAssetData,
    ],
  );

  const handleSavingsPlanFrequencyChange = useCallback(
    (freq: SavingsPlanFrequency) => {
      setSavingsPlanFrequency(freq);

      if (freq === SP_WEEKLY_FREQUENCY || freq === SP_BIWEEKLY_FREQUENCY) {
        const isCurrentlyWeeklyLike =
          savingsPlanFrequency === SP_WEEKLY_FREQUENCY ||
          savingsPlanFrequency === SP_BIWEEKLY_FREQUENCY;

        if (!isCurrentlyWeeklyLike) {
          const now = new Date();

          setSavingsPlanRecurringDate(
            format(
              new Date(now.getFullYear(), now.getMonth(), 1),
              'yyyy-MM-dd',
            ),
          ); // FOR weekly and biweekly SPs, BE expects the recurrence date to be a date like 01-08-2026 to 07-08-2026, and decides the chosen day of the week based on the 01 to 07 index
          // for this reason, we set the selected default date for weekly and biweekly frequency to the first day of the current month; this will be displayed as MONDAY by WeekDayPicker
        }
      } else if (freq === SP_DAILY_FREQUENCY) {
        const now = new Date();

        setSavingsPlanRecurringDate(format(now, 'yyyy-MM-dd'));
      } else {
        setSavingsPlanRecurringDate(
          getYmdForNthDayFromToday(minimumCalendarDaysBeforeExecution),
        );
      }
    },
    [minimumCalendarDaysBeforeExecution, savingsPlanFrequency],
  );

  return {
    formState: {
      isLoading,
      displayType,
      inputValue,
      errors,
      formHasAnyErrors,
      allButtonsDisabled,
      inputPlaceholder,
      inputContextData,
      convertedSummary,
      savingsPlanRecurringDate,
      savingsPlanFrequency,
    },
    handlers: {
      onFormSubmit,
      onTradeInputChange,
      onQuickSelectButtonClick,
      onInputBlur,
      onChangeDisplayType,
      setSavingsPlanRecurringDate,
      setSavingsPlanFrequency: handleSavingsPlanFrequencyChange,
    },
  };
};
